import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { useEffect, useRef, useState } from 'react';
import { DisplayItem } from '#/types/message';
import Slider from 'react-slick';
import styles from './content-modal-mobile.module.css';
import TableDisplay from '../messages/content-carousel/table-display/table-display';
import ImageContainer from '../messages/content-carousel/image-container/image-container';
import VideoContainer from '../messages/content-carousel/video-container/video-container';

interface ContentModalMobileProps {
  isOpen: boolean;
  currentItem: DisplayItem | null;
  items: DisplayItem[];
  activeIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

export function ContentModalMobile({
  isOpen,
  currentItem,
  items,
  activeIndex,
  onClose,
  onNext,
  onPrevious,
}: ContentModalMobileProps) {
  const transformWrapperRef = useRef<ReactZoomPanPinchRef>(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const handleZoomChange = (ref: ReactZoomPanPinchRef) => {
    const scale = ref.state.scale;
    setIsZoomed(scale > 1); // Consider zoomed if scale > 1 (100%)
  };

  const sliderSettings = {
    dots: true,
    infinite: items.length > 1,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: !isZoomed,
    swipe: !isZoomed,
    touchMove: !isZoomed,
    draggable: false,
    adaptiveHeight: false,
    variableWidth: false,
    vertical: false,
    verticalSwiping: false,
    centerMode: false,
    focusOnSelect: false,
    initialSlide: activeIndex,
    touchThreshold: 10,
    swipeToSlide: true,
    beforeChange: () => {
      if (transformWrapperRef.current) {
        transformWrapperRef.current.resetTransform();
        setIsZoomed(false);
      }

      // Focus management
      const allSlides = document.querySelectorAll('.slick-slide');
      allSlides.forEach(slide => {
        const focusableElements = slide.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), img[onclick], div[onclick]'
        );
        focusableElements.forEach((element: any) => {
          if (element.blur) {
            element.blur();
          }
        });
      });

      if (document.activeElement && document.activeElement !== document.body) {
        (document.activeElement as HTMLElement).blur();
      }
    },
    afterChange: (current: number) => {
      if (current !== activeIndex) {
        if (current > activeIndex) {
          onNext();
        } else {
          onPrevious();
        }
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      window.history.pushState({ modalOpen: true }, '', window.location.pathname);
      const handlePopState = () => {
        onClose();
      };
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
        if (window.history.state && window.history.state.modalOpen) {
        }
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen || !currentItem) return null;

  return (
    <>
      <div className={styles.modal} >
        <div className={styles.sliderWrapper}>
          <Slider {...sliderSettings}>
            {items.map((item, index) => (
              <div key={index}>
                <div className={styles.slideContent}>
                  <TransformWrapper
                    ref={index === activeIndex ? transformWrapperRef : undefined}
                    initialScale={1}
                    centerOnInit={true}
                    minScale={0.75}
                    maxScale={8}
                    wheel={{ step: 0.2 }}
                    doubleClick={{ disabled: false, step: 0.7 }}
                    panning={{
                      disabled: !isZoomed,
                      velocityDisabled: true,
                    }}
                    pinch={{
                      disabled: false,
                    }}
                    onZoom={(ref) => handleZoomChange(ref)}
                    onPanning={(ref) => handleZoomChange(ref)}
                    onPinching={(ref) => handleZoomChange(ref)}
                    limitToBounds={true}
                    centerZoomedOut={true}
                  >
                    <div
                      className={styles.closeWrapper}
                      onClick={() => {
                        onClose();
                      }}
                    >
                      <TransformComponent
                        wrapperClass={styles.transformWrapperStyles}
                        wrapperStyle={{
                          width: '100%',
                          // when not zoomed, shrink-wrap around content (centered block)
                          // when zoomed, turn into a full-screen viewport
                          height: isZoomed ? '100dvh' : 'auto',
                          cursor: isZoomed ? 'grab' : 'pointer',
                        }}
                        contentClass={styles.transformContentStyles}
                        contentStyle={{
                          backgroundColor: '#f0f0f0',
                          borderRadius: '0.2rem',
                          padding: '0.2rem',
                          cursor: isZoomed ? 'grab' : 'pointer',
                        }}
                      >
                        {item.item_type === 'image' ? (
                          <ImageContainer
                            image={item}
                            onImageClick={() => { }}
                            maxDisplayWidth={window.innerWidth}
                            maxDisplayHeight={window.innerHeight}
                            internalActiveIndex={index}
                            forceDefaultLayout={false}
                          />
                        ) : item.item_type === 'table' ? (
                          <div className={styles.tableContentWrapper}>
                            <TableDisplay table={item} />
                          </div>
                        ) : item.item_type === 'video' ? (
                          <VideoContainer
                            video={item}
                            maxDisplayWidth={window.innerWidth}
                            maxDisplayHeight={window.innerHeight}
                            internalActiveIndex={activeIndex}
                          />
                        ) : (
                          <div className={styles.unsupportedContent}>
                            Content not supported in modal
                          </div>
                        )}
                      </TransformComponent>
                    </div>
                  </TransformWrapper>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>
      <button
        className={styles.closeButton}
        onClick={onClose}
        aria-label="Close modal"
      >
        ×
      </button>
    </>
  );
}